import { Business, Description, Person } from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Box,
  Stack,
  Tab,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { ElementType, SyntheticEvent } from "react";

import { ConsentAccordion } from "./ConsentAccordion";

type ConsentTabValue = "1" | "2" | "3";

const CONSENT_TAB_ITEMS: {
  value: ConsentTabValue;
  label: string;
  /** Rótulo mobile com quebra de linha (`\n`). */
  mobileLabel: string;
  icon: ElementType;
}[] = [
  { value: "1", label: "Pessoa", mobileLabel: "Pessoa", icon: Person },
  {
    value: "2",
    label: "Unidade de saúde",
    mobileLabel: "Unidade de\nsaúde",
    icon: Business,
  },
  {
    value: "3",
    label: "Documentos clínicos",
    mobileLabel: "Documentos\nclínicos",
    icon: Description,
  },
];

function filterConsentRows(rows: any[] | undefined, searchField: string) {
  return rows?.filter((filter: any) =>
    searchField
      ? filter.name?.toLowerCase().includes(searchField) ||
        filter.identifier?.includes(searchField) ||
        filter.hora?.includes(searchField)
      : true,
  );
}

/** Abas pill no mobile — mesmo padrão do dashboard (questionários). */
function ConsentMobileTabBar({
  tabs,
  selectedTab,
  onChange,
}: {
  tabs: typeof CONSENT_TAB_ITEMS;
  selectedTab: string;
  onChange: (value: ConsentTabValue) => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        bgcolor: (t) => t.palette.action.hover,
        borderRadius: 2,
        p: 0.5,
        mb: 1.25,
      }}
    >
      {tabs.map((tab) => {
        const isActive = selectedTab === tab.value;
        const Icon = tab.icon;

        return (
          <Box
            key={tab.value}
            component="button"
            type="button"
            onClick={() => onChange(tab.value)}
            sx={{
              flex: 1,
              minWidth: 0,
              border: "none",
              cursor: "pointer",
              borderRadius: 1.5,
              py: 0.75,
              px: 0.5,
              fontFamily: "inherit",
              transition: "all 0.18s ease",
              minHeight: 52,
              bgcolor: isActive ? "background.paper" : "transparent",
              color: isActive ? "primary.main" : "text.secondary",
              boxShadow: isActive ? "0 1px 4px rgba(0,0,0,0.12)" : "none",
              WebkitTapHighlightColor: "transparent",
              "&:active": { transform: "scale(0.98)" },
            }}
          >
            <Stack alignItems="center" spacing={0.35} sx={{ minWidth: 0 }}>
              <Icon sx={{ fontSize: 20, flexShrink: 0 }} />
              <Typography
                component="span"
                variant="caption"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.625rem",
                  lineHeight: 1.2,
                  textAlign: "center",
                  whiteSpace: "pre-line",
                  wordBreak: "normal",
                  maxWidth: "100%",
                  px: 0.15,
                }}
              >
                {tab.mobileLabel}
              </Typography>
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}

export function ConsentTabs({
  granted,
  received,
  searchField,
  selectedTab,
  handleChangeTab,
  /** Somente abas Pessoa e Unidade, apenas listas “Concedidos” (ex.: fluxo wearable). */
  embedGrantedOnly = false,
}: any) {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));

  const visibleTabs = embedGrantedOnly
    ? CONSENT_TAB_ITEMS.filter((tab) => tab.value !== "3")
    : CONSENT_TAB_ITEMS;

  const handleMobileTabChange = (value: ConsentTabValue) => {
    handleChangeTab({} as SyntheticEvent, value);
  };

  return (
    <Stack width="100%">
      <TabContext value={selectedTab}>
        {isMobileLayout ? (
          <ConsentMobileTabBar
            tabs={visibleTabs}
            selectedTab={selectedTab}
            onChange={handleMobileTabChange}
          />
        ) : (
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <TabList onChange={handleChangeTab} variant="scrollable">
              {visibleTabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <Tab
                    key={tab.value}
                    value={tab.value}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Icon sx={{ mr: 1, fontSize: 20 }} />
                        {tab.label}
                      </Box>
                    }
                  />
                );
              })}
            </TabList>
          </Box>
        )}

        <TabPanel sx={{ padding: 0, pt: isMobileLayout ? 0 : undefined }} value="1">
          {!embedGrantedOnly && (
            <ConsentAccordion
              selectedTab={selectedTab}
              title="Recebidos"
              type="received"
              data={filterConsentRows(received, searchField)}
            />
          )}
          <ConsentAccordion
            selectedTab={selectedTab}
            title="Concedidos"
            type="granted"
            data={filterConsentRows(granted?.[0], searchField)}
          />
        </TabPanel>

        <TabPanel sx={{ padding: 0, pt: isMobileLayout ? 0 : undefined }} value="2">
          <ConsentAccordion
            defaultOpen
            selectedTab={selectedTab}
            title="Concedidos"
            type="granted"
            data={filterConsentRows(granted?.[1], searchField)}
          />
        </TabPanel>

        {!embedGrantedOnly && (
          <TabPanel sx={{ padding: 0, pt: isMobileLayout ? 0 : undefined }} value="3">
            <ConsentAccordion
              defaultOpen
              selectedTab={selectedTab}
              title="Documentos"
              type="granted"
              data={filterConsentRows(granted?.[2], searchField)}
            />
          </TabPanel>
        )}
      </TabContext>
    </Stack>
  );
}
